import { onScopeDispose, getCurrentScope } from "vue";

const DEFAULT_TIME = 1000 * 60 * 5;
const DEFAULT_MAX_SIZE = 100;
const CACHE_MISS = Symbol("CACHE_MISS");

export const useCache = (
  { time = DEFAULT_TIME, maxSize = DEFAULT_MAX_SIZE } = {
    time: DEFAULT_TIME,
    maxSize: DEFAULT_MAX_SIZE,
  },
) => {
  const cache = new Map();

  const isExpired = createdAt => Date.now() - createdAt >= time;

  const pruneExpired = () => {
    for (const [key, entry] of cache.entries()) {
      if (entry.hasData && isExpired(entry.createdAt)) {
        cache.delete(key);
      } else if (entry.pending && isExpired(entry.createdAt)) {
        cache.delete(key);
      }
    }
  };

  const enforceSizeLimit = () => {
    if (maxSize && cache.size > maxSize) {
      const entries = Array.from(cache.entries());
      // Only consider non-pending entries with data for LRU eviction
      const evictionCandidates = entries.filter(([, entry]) => !entry.pending && entry.hasData);

      if (!evictionCandidates.length) {
        // All entries are pending or otherwise not safe to evict; skip eviction for now
        return;
      }

      const excess = cache.size - maxSize;
      const toDelete = evictionCandidates
        .sort(([, a], [, b]) => a.lastAccessAt - b.lastAccessAt)
        .slice(0, Math.min(excess, evictionCandidates.length));

      toDelete.forEach(([key]) => cache.delete(key));
    }
  };

  const get = key => {
    if (!cache.has(key)) return CACHE_MISS;

    const entry = cache.get(key);
    if (entry.pending) {
      if (isExpired(entry.createdAt)) {
        cache.delete(key);
        return CACHE_MISS;
      }
      return CACHE_MISS;
    }

    if (!entry.hasData || isExpired(entry.createdAt)) {
      cache.delete(key);
      return CACHE_MISS;
    }

    // Update last access time for LRU
    entry.lastAccessAt = Date.now();
    return entry.data;
  };

  const set = (key, data) => {
    pruneExpired();
    const now = Date.now();
    cache.set(key, {
      data,
      createdAt: now,
      lastAccessAt: now,
      pending: false,
      hasData: true,
    });
    enforceSizeLimit();
  };

  const clear = key => {
    if (key !== undefined) {
      cache.delete(key);
    } else {
      cache.clear();
    }
  };

  if (getCurrentScope()) {
    onScopeDispose(clear);
  }

  const cacheable = async (
    fn,
    key = "default",
    forceUpdate = false,
    shouldCache = r => r != null,
  ) => {
    if (!forceUpdate) {
      const cached = get(key);
      if (cached !== CACHE_MISS) {
        return cached;
      }
    }

    const entry = cache.get(key);
    if (!forceUpdate && entry?.pending && !isExpired(entry.createdAt)) {
      return entry.promise;
    }

    const promise = Promise.resolve()
      .then(fn)
      .then(result => {
        // Only update cache if this promise is still current
        const currentEntry = cache.get(key);
        if (currentEntry?.promise === promise) {
          if (shouldCache(result)) {
            set(key, result);
          } else {
            cache.delete(key);
          }
        }
        return result;
      })
      .catch(error => {
        // Only delete from cache if this promise is still current
        const currentEntry = cache.get(key);
        if (currentEntry?.promise === promise) {
          cache.delete(key);
        }
        throw error;
      });

    const now = Date.now();
    cache.set(key, {
      promise,
      pending: true,
      hasData: false,
      createdAt: now,
      lastAccessAt: now,
    });
    return promise;
  };

  return { cacheable, clear, get };
};
